import React, { useMemo } from "react";
import { Chart } from "@tanstack/charts/react";
import { colorLegend, defineChart } from "@tanstack/charts";
import { pie, polar, radialArc, radialText, type PieDatum } from "@tanstack/charts/polar";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip, type ChartTooltipContent, type ChartTooltipContentContext } from "@tanstack/charts/tooltip";

import { ChartFrame } from "./ChartFrame";
import { COLORWAY } from "./chartTheme";

export interface DonutSlice {
    label: string;
    value: number;
}

function percentText(fraction: number): string {
    return (fraction * 100).toFixed(0) + "%";
}

type AllocatedSlice = PieDatum<{ label: string; value: number }>;
type DonutChartDefinition = React.ComponentProps<typeof Chart>["definition"];

function sliceLabelFontSize(fraction: number): number {
    if (fraction >= 0.12) return 11;
    if (fraction >= 0.06) return 9;
    if (fraction >= 0.03) return 8;
    return 7;
}

/** Pixel offset from center for each label row (name above, percent below). */
function sliceLabelLineOffset(fontSize: number): number {
    return (fontSize + 6) / 2;
}

function donutPieTooltipContent(total: number, points: readonly unknown[], _context: ChartTooltipContentContext): ChartTooltipContent {
    const point = points[0] as { datum?: unknown; groupLabel?: string; color?: string } | undefined;
    if (!point) {
        return { rows: [] };
    }
    const datum = point.datum as { label?: string; value?: number; fraction?: number } | undefined;
    const key = datum?.label ?? point.groupLabel ?? "";
    let fraction = datum?.fraction;
    if (typeof fraction !== "number" && typeof datum?.value === "number" && total > 0) {
        fraction = datum.value / total;
    }
    let valueText = "";
    if (typeof fraction === "number" && Number.isFinite(fraction)) {
        valueText = percentText(fraction);
    } else if (typeof datum?.value === "number") {
        valueText = datum.value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    const content: ChartTooltipContent = { rows: [] };
    if (key) content.title = key;
    if (point.color) content.color = point.color;
    if (valueText) content.rows = [{ label: "", value: valueText }];
    return content;
}

function pieSliceLabelMarks(allocated: AllocatedSlice[], labelMode: "percent" | "label+percent", labelRadius: number) {
    const byFontSize = new Map<number, AllocatedSlice[]>();
    for (const slice of allocated) {
        const fontSize = sliceLabelFontSize(slice.fraction);
        const group = byFontSize.get(fontSize);
        if (group) group.push(slice);
        else byFontSize.set(fontSize, [slice]);
    }
    const marks: ReturnType<typeof radialText>[] = [];
    for (const [fontSize, group] of Array.from(byFontSize.entries())) {
        const lineOffset = sliceLabelLineOffset(fontSize);
        if (labelMode === "label+percent") {
            marks.push(
                radialText(group, {
                    angle: "angle",
                    radius: labelRadius,
                    text: (slice) => slice.label,
                    fill: "#ffffff",
                    fontSize,
                    anchor: "middle",
                    baseline: "middle",
                    dy: -lineOffset,
                    key: (slice) => `${slice.label}:name`
                })
            );
        }
        marks.push(
            radialText(group, {
                angle: "angle",
                radius: labelRadius,
                text: (slice) => percentText(slice.fraction),
                fill: "#ffffff",
                fontSize,
                anchor: "middle",
                baseline: "middle",
                dy: labelMode === "label+percent" ? lineOffset : 0,
                key: (slice) => `${slice.label}:pct`
            })
        );
    }
    return marks;
}

export function DonutChart({
    slices,
    colors,
    hole = 0.4,
    showLegend = false,
    labelMode = "label+percent",
    ariaLabel,
    ariaDescription,
    height = 320,
    width,
    style,
    className,
    onSelect
}: {
    slices: DonutSlice[];
    colors?: string[];
    hole?: number;
    showLegend?: boolean;
    labelMode?: "none" | "percent" | "label+percent";
    ariaLabel: string;
    ariaDescription?: string;
    height?: number;
    width?: number;
    style?: React.CSSProperties;
    className?: string;
    onSelect?: (label: string) => void;
}): React.ReactElement {
    const rows = useMemo(
        () =>
            slices
                .filter((slice) => typeof slice.value === "number" && Number.isFinite(slice.value) && slice.value > 0)
                .map((slice) => ({ label: String(slice.label), value: slice.value })),
        [slices]
    );
    const palette = colors && colors.length > 0 ? colors : COLORWAY;
    const definition = useMemo(() => {
        const total = rows.reduce((sum, row) => sum + row.value, 0);
        const pieTooltip = {
            use: tooltip,
            content: (points: readonly unknown[], context: ChartTooltipContentContext) => donutPieTooltipContent(total, points, context)
        };
        const allocated = pie(rows, { value: "value" });
        const labelRadius = hole > 0 ? (hole + 1) / 2 : 0.62;
        const arc = radialArc(allocated, {
            innerRadius: ({ radius }) => radius * hole,
            color: "label",
            key: "label"
        });
        const color = {
            domain: rows.map((row) => row.label),
            range: rows.map((_, index) => palette[index % palette.length]),
            legend: showLegend ? colorLegend() : undefined
        };
        if (labelMode === "none") {
            return defineChart({
                marks: [
                    polar({
                        inset: 8,
                        radiusRatio: showLegend ? 0.78 : 0.92,
                        marks: [arc],
                        scales: { angle: null, radius: null }
                    })
                ],
                scales: { x: null, y: null },
                color,
                tooltip: pieTooltip
            }) as DonutChartDefinition;
        }
        return defineChart({
            marks: [
                polar({
                    inset: 8,
                    radiusRatio: showLegend ? 0.78 : 0.92,
                    marks: [arc, ...pieSliceLabelMarks(allocated, labelMode, labelRadius)],
                    scales: {
                        angle: { scale: scaleLinear().domain([0, Math.PI * 2]) },
                        radius: { scale: scaleLinear().domain([0, 1]) }
                    }
                })
            ],
            scales: { x: null, y: null },
            color,
            tooltip: pieTooltip
        }) as DonutChartDefinition;
    }, [hole, labelMode, palette, rows, showLegend]);

    if (rows.length === 0) return <div />;

    return (
        <ChartFrame
            definition={definition}
            ariaLabel={ariaLabel}
            ariaDescription={ariaDescription ?? rows.map((row) => `${row.label} ${row.value}`).join(", ")}
            height={height}
            width={width}
            style={style}
            className={className}
            onSelect={onSelect ? (datum) => onSelect(String(datum?.label ?? "")) : undefined}
        />
    );
}
